import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Play, Search, CheckCircle2, TrendingUp, DollarSign, FileText, Users, ChevronRight, Eye,
} from "lucide-react";
import { toast } from "sonner";
import JeaCard from "@/components/jea/JeaCard";
import JeaButton from "@/components/jea/JeaButton";
import JeaSelect from "@/components/jea/JeaSelect";
import Skeleton from "@/components/jea/Skeleton";
import EmptyState from "@/components/jea/EmptyState";
import RunQueryModal from "@/components/queries/RunQueryModal";
import { useAuth } from "@/context/AuthContext";
import { usePreviewGate } from "@/hooks/usePreviewGate";
import { apiClient, formatApiError } from "@/lib/apiClient";
import { centsToUSD, shortDate, relativeTime } from "@/lib/dates";
import { cn } from "@/lib/utils";

const StatCard = ({ label, value, Icon, sub }) => (
  <motion.div
    initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.25 }}
    className="rounded-[12px] bg-bg-secondary border border-border-default p-5"
  >
    <div className="flex items-center justify-between">
      <div className="jea-caption">{label}</div>
      {Icon && <Icon size={16} className="text-text-muted" />}
    </div>
    <div className="jea-mono font-extrabold text-[28px] mt-2 text-white">{value}</div>
    {sub && <div className="text-text-muted text-[11px] mt-1">{sub}</div>}
  </motion.div>
);

const daysUntil = (iso) => {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return null;
  return Math.round((t - Date.now()) / (1000 * 60 * 60 * 24));
};

const DueBadge = ({ dueDays }) => {
  if (dueDays === null || dueDays === undefined) return null;
  const overdue = dueDays < 0;
  const soon = dueDays >= 0 && dueDays <= 7;
  const tone = overdue
    ? "bg-danger/15 text-danger border-danger/40"
    : soon
      ? "bg-warning/15 text-warning border-warning/40"
      : "bg-bg-tertiary text-text-secondary border-border-default";
  return (
    <span className={cn("inline-flex px-2 py-1 rounded-full text-[10px] font-bold tracking-[0.06em] uppercase border", tone)}>
      {overdue ? `${Math.abs(dueDays)}d overdue` : dueDays === 0 ? "Due today" : `Due in ${dueDays}d`}
    </span>
  );
};

const RunQueryPage = () => {
  const nav = useNavigate();
  const { user } = useAuth();
  const { inPreview, guardAction } = usePreviewGate();

  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);

  const [debtors, setDebtors] = useState([]);
  const [debtorsLoading, setDebtorsLoading] = useState(true);

  const [runs, setRuns] = useState([]);
  const [runsLoading, setRunsLoading] = useState(true);

  const [pickedDebtorId, setPickedDebtorId] = useState("");
  const [runFor, setRunFor] = useState(null); // debtor to launch modal for

  useEffect(() => {
    (async () => {
      try {
        const { data } = await apiClient.get("/queries/stats");
        setStats(data);
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setStatsLoading(false); }
    })();
    (async () => {
      try {
        const { data } = await apiClient.get("/debtors");
        setDebtors((data.debtors || []).filter((d) => d.status !== "archived"));
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setDebtorsLoading(false); }
    })();
    (async () => {
      try {
        const { data } = await apiClient.get("/queries/recent?limit=10");
        setRuns(data.runs || []);
      } catch (e) { /* endpoint may not exist yet */ }
      finally { setRunsLoading(false); }
    })();
  }, []);

  const dueSoon = useMemo(() => {
    const rows = debtors
      .map((d) => ({ ...d, _due: daysUntil(d.next_query_at) }))
      .filter((d) => d._due !== null && d._due <= 7);
    rows.sort((a, b) => a._due - b._due);
    return rows.slice(0, 8);
  }, [debtors]);

  const nextEver = useMemo(() => {
    const rows = debtors
      .map((d) => ({ ...d, _due: daysUntil(d.next_query_at) }))
      .filter((d) => d._due !== null);
    rows.sort((a, b) => a._due - b._due);
    return rows[0] || null;
  }, [debtors]);

  const launchRun = guardAction((debtor) => setRunFor(debtor), { feature: "running queries" });

  const debtorOptions = useMemo(
    () => debtors.map((d) => ({ value: d.id, label: `${d.full_name} · ${d.judgment?.case_number || "—"}` })),
    [debtors]
  );

  return (
    <div className="space-y-6" data-testid="run-query-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div className="space-y-1">
          <div className="jea-caption">Broker Query Engine</div>
          <h1 className="text-[32px] font-extrabold tracking-[-0.02em] text-white">Run Query</h1>
          <p className="text-text-secondary text-[14px]">Sweep brokers for asset, court, and skip-trace data on any judgment.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="run-query-stats">
        {statsLoading ? (
          Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-[104px] rounded-[12px]" />)
        ) : (
          <>
            <StatCard label="Queries This Month" value={stats?.queries_this_month ?? 0} Icon={Search} />
            <StatCard label="Spend This Month" value={centsToUSD(stats?.spend_this_month_cents || 0)} Icon={DollarSign} />
            <StatCard label="Reports Total" value={stats?.reports_total ?? 0} Icon={FileText} />
            <StatCard label="Judgments Ready" value={dueSoon.length} Icon={Users}
                      sub={dueSoon.length === 0 && nextEver ? `Next: ${shortDate(nextEver.next_query_at)}` : ""} />
          </>
        )}
      </div>

      <section data-testid="run-query-due-section">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-white font-bold text-[16px]">Judgments Ready for Query</h2>
          <Link to="/debtors" className="text-[12px] text-text-secondary hover:text-accent-pink inline-flex items-center gap-1">
            View all judgments <ChevronRight size={12} />
          </Link>
        </div>
        {debtorsLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-[120px]" />)}
          </div>
        ) : dueSoon.length === 0 ? (
          <EmptyState
            icon={CheckCircle2}
            title="All judgments are current."
            description={nextEver ? `Next scheduled query: ${shortDate(nextEver.next_query_at)} (${nextEver.full_name}).` : "You'll see cards here when a judgment's next scheduled query is within 7 days."}
            testid="run-query-due-empty"
          />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {dueSoon.map((d) => (
              <motion.div
                key={d.id}
                initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                className="rounded-[12px] bg-bg-secondary border border-border-default p-5 flex flex-col gap-3"
                data-testid={`run-query-due-card-${d.id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="min-w-0">
                    <Link
                      to={`/debtors/${d.id}`}
                      className="text-white font-bold text-[15px] hover:text-accent-pink transition-colors truncate block"
                    >
                      {d.full_name}
                    </Link>
                    <div className="jea-caseno text-[12px] mt-0.5">{d.judgment?.case_number || "—"}</div>
                  </div>
                  <DueBadge dueDays={d._due} />
                </div>
                <div className="flex items-center justify-between text-[12px]">
                  <div className="text-text-secondary">Next</div>
                  <div className="jea-date">{shortDate(d.next_query_at)}</div>
                </div>
                <div className="flex items-center justify-between text-[12px]">
                  <div className="text-text-secondary">Judgment</div>
                  <div className="jea-mono text-accent-pink font-bold">
                    {centsToUSD(d.judgment?.amount_cents || 0)}
                  </div>
                </div>
                <JeaButton
                  size="sm"
                  onClick={() => launchRun(d)}
                  className="gap-2 mt-2"
                  data-testid={`run-query-due-btn-${d.id}`}
                >
                  <Play size={12} /> Run Query Now
                </JeaButton>
              </motion.div>
            ))}
          </div>
        )}
      </section>

      <section data-testid="run-query-recent-section">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-white font-bold text-[16px]">Recent Query Runs</h2>
          <Link to="/reports" className="text-[12px] text-text-secondary hover:text-accent-pink inline-flex items-center gap-1">
            View All Reports <ChevronRight size={12} />
          </Link>
        </div>
        {runsLoading ? (
          <JeaCard className="p-4 space-y-2">
            {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-12" />)}
          </JeaCard>
        ) : runs.length === 0 ? (
          <EmptyState
            icon={TrendingUp}
            title="No queries yet"
            description="Run your first broker query to see it show up here."
            testid="run-query-recent-empty"
          />
        ) : (
          <JeaCard className="p-0 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border-default">
                    <th className="jea-caption text-text-secondary p-4 text-left">Debtor</th>
                    <th className="jea-caption text-text-secondary p-4 text-left">Date</th>
                    <th className="jea-caption text-text-secondary p-4 text-left">Brokers</th>
                    <th className="jea-caption text-text-secondary p-4 text-left">Status</th>
                    <th className="jea-caption text-text-secondary p-4 text-right">Cost</th>
                    <th className="jea-caption text-text-secondary p-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {runs.map((r) => (
                    <tr key={r.id} className="border-b border-border-default hover:bg-bg-tertiary" data-testid={`recent-run-${r.id}`}>
                      <td className="p-4 text-white font-semibold text-[13px]">{r.debtor_snapshot?.full_name || "—"}</td>
                      <td className="p-4 jea-date text-[13px]">{relativeTime(r.started_at)}</td>
                      <td className="p-4 text-text-secondary text-[13px] jea-mono">{(r.broker_slugs || []).length}</td>
                      <td className="p-4">
                        <span className="inline-flex px-2 py-1 rounded-full text-[10px] font-bold tracking-[0.06em] uppercase bg-success/15 text-success border border-success/40">
                          {r.status || "—"}
                        </span>
                      </td>
                      <td className="p-4 jea-mono text-accent-pink font-bold text-[13px] text-right">{centsToUSD(r.total_cost_cents)}</td>
                      <td className="p-4 text-right">
                        <button
                          type="button"
                          onClick={() => nav(`/debtors/${r.debtor_id}/queries/${r.id}`)}
                          className="w-8 h-8 rounded-[6px] border border-border-default text-text-secondary hover:text-accent-pink hover:border-accent-pink/50 transition-colors inline-flex items-center justify-center"
                          data-testid={`recent-run-view-${r.id}`}
                        >
                          <Eye size={13} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </JeaCard>
        )}
      </section>

      <section data-testid="run-query-picker-section">
        <JeaCard>
          <div className="jea-caption mb-2">Run a Query on Any Judgment</div>
          <p className="text-text-secondary text-[13px] mb-4">
            Pick a judgment to launch the Run Query modal directly.
          </p>
          <div className="flex items-end gap-3 flex-wrap">
            <div className="flex-1 min-w-[280px]">
              <JeaSelect
                value={pickedDebtorId}
                onChange={setPickedDebtorId}
                options={debtorOptions}
                placeholder="Select a judgment…"
                data-testid="run-query-debtor-picker"
              />
            </div>
            <JeaButton
              onClick={() => {
                const d = debtors.find((x) => x.id === pickedDebtorId);
                if (d) launchRun(d);
              }}
              disabled={!pickedDebtorId}
              className="gap-2"
              data-testid="run-query-continue"
            >
              Continue <ChevronRight size={13} />
            </JeaButton>
          </div>
        </JeaCard>
      </section>

      <RunQueryModal
        open={!!runFor}
        debtor={runFor}
        onClose={() => setRunFor(null)}
        onCompleted={(payload) => {
          setRunFor(null);
          if (payload?.query_run?.id && payload?.query_run?.debtor_id) {
            nav(`/debtors/${payload.query_run.debtor_id}/queries/${payload.query_run.id}`);
          }
        }}
      />
    </div>
  );
};

export default RunQueryPage;
